import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, InjectOptions, PLATFORM_ID, ProviderToken, inject } from '@angular/core';
import { MixinConstructor } from '../mixin/mixin-base';
import { LocalStorageService } from '../wrappers/local-storage.service';
import { SessionStorageService } from '../wrappers/session-storage.service';

export const safariInjectorMixin = <B extends MixinConstructor>(Base: B) =>
  class SafariInjectorMixin extends Base {
    /**
   * This function wraps angular's inject function and is the only way we should be injecting
   * in smart components. 
   * 
   * To Mock: 
   * 
   * vi.spyOn(SafariSmartComponent.prototype, 'inject').mockImplementation() 
   * 
   * OR (if desiring to do partial injections)
   * 
   * vi.spyOn(SafariSmartComponent.prototype, 'inject').mockImplementation((token: ProviderToken<any>) => {
      
      if (token == Store || token == Actions) {
        return TestBed.inject(token); <-- assumes those are provided in the testbed
      }
      if (token == FeatureFlagsService) {
        return {
          ...somefakeclass <-- return some fake value
        }
      }
      return null;
    });
   * @param token Same as angular's inject function
   * @returns Same as angular's inject function
   */
    inject<T>(token: ProviderToken<T>, options: InjectOptions = { optional: false }): T {
      // eslint-disable-next-line no-restricted-syntax -- framework-level so it's ok
      return inject(token, options);
    }
  };

/**
 * Specialized version of SafariInjector that checks if the platform is browser
 * Should only be used in places that access windows or localstorage or other browser-specific APIs
 * NOTE: Aboslutely can not be used with adapters because currently we new-up gar/lit/oth/dr adapters
 * inside setMatterServices function, which is called in the global scope of LpmsDataAccessModule.
 * Because of that the function doesn't have an injection context and can't use inject() function.
 */
export class SafariInjector extends safariInjectorMixin(class {}) {
  #platformId: object | null = null;
  document: Document = null;
  localStorage: LocalStorageService;
  sessionStorage: SessionStorageService;
  constructor() {
    super();
    this.#platformId = this.inject(PLATFORM_ID);
    this.document = this.inject(DOCUMENT);
    this.localStorage = this.inject(LocalStorageService);
    this.sessionStorage = this.inject(SessionStorageService);
  }
  get isPlatformBrowser(): boolean {
    return isPlatformBrowser(this.#platformId);
  }
  async clearAppCache() {
    if (!this.isPlatformBrowser) {
      return;
    }
    const swPromise = 'serviceWorker' in navigator ? navigator.serviceWorker.getRegistrations().then(regs => Promise.all(regs.map(r => r.unregister()))) : Promise.resolve();
    const cachePromise = typeof caches !== 'undefined' ? caches.keys().then(names => Promise.all(names.map(name => caches.delete(name)))) : Promise.resolve();
    await Promise.allSettled([swPromise, cachePromise]);
    this.localStorage?.clear();
  }
}
